/**
 * Session Management Constants
 *
 * Server-enforced session timeout configuration.
 * These values are used by both client and server (Cloud Functions).
 */

// ============================================================================
// TIMEOUT CONFIGURATION
// ============================================================================

/** Total inactivity time before session expires (45 minutes) */
export const SESSION_INACTIVITY_TIMEOUT_MS = 45 * 60 * 1000;

/** Time before expiry to show warning modal (15 minutes) */
export const SESSION_WARNING_BEFORE_TIMEOUT_MS = 15 * 60 * 1000;

/** How often client sends heartbeat to server (60 seconds) */
export const SESSION_HEARTBEAT_INTERVAL_MS = 60 * 1000;

/** Grace period for network delays before considering session stale (2 heartbeats) */
export const SESSION_STALE_THRESHOLD_MS = SESSION_HEARTBEAT_INTERVAL_MS * 2;

// ============================================================================
// SESSION STATUS TYPES
// ============================================================================

export type SessionStatus = 'active' | 'warning' | 'expired';

// ============================================================================
// SESSION DOCUMENT SCHEMA (Firestore: users/{userId}/sessions/{sessionId})
// ============================================================================

export interface Session {
  id: string;
  userId: string;
  deviceInfo: DeviceInfo;
  createdAt: Date;
  lastActivityAt: Date;
  expiresAt: Date;
  active: boolean;
}

export interface DeviceInfo {
  userAgent: string;
  browser: string;
  os: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'other';
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface CreateSessionRequest {
  userAgent: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  expiresAt: Date;
}

export interface HeartbeatRequest {
  sessionId: string;
}

export interface HeartbeatResponse {
  status: SessionStatus;
  expiresAt: Date;
  remainingMs: number;
}

export interface GetSessionStatusRequest {
  sessionId: string;
}

export interface GetSessionStatusResponse {
  status: SessionStatus;
  expiresAt: Date;
  remainingMs: number;
}

export interface EndSessionRequest {
  sessionId: string;
}

export interface EndOtherSessionsRequest {
  currentSessionId: string;
}

export interface EndOtherSessionsResponse {
  endedCount: number;
}

export interface GetActiveSessionsResponse {
  sessions: SessionInfo[];
}

export interface SessionInfo {
  id: string;
  deviceInfo: DeviceInfo;
  createdAt: Date;
  lastActivityAt: Date;
  isCurrent: boolean;
}

// ============================================================================
// ERROR CODES
// ============================================================================

export const SESSION_ERROR_CODES = {
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  INVALID_SESSION: 'INVALID_SESSION',
  UNAUTHORIZED: 'UNAUTHORIZED',
} as const;

export type SessionErrorCode = typeof SESSION_ERROR_CODES[keyof typeof SESSION_ERROR_CODES];
